import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';

// v2: binary mode. Checked = needs image remaster. Unchecked = keep.
const STORAGE_KEY = 'vitalrx_image_triage_v2';

const resolveImagePath = (image: string | null | undefined): string => {
  if (!image) return '';
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  if (image.startsWith('/')) return image;
  return `/images/${image}`;
};

const formatCategoryName = (name: string | undefined | null): string => {
  if (!name) return '—';
  return name
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const AdminImageTriage: React.FC = () => {
  const { products, isLoading } = useSupabaseData();

  // Items with an image
  const allItems = useMemo(
    () => (products || []).filter(p => p.image && p.image.length > 0),
    [products]
  );

  // Selected = needs remaster
  const [selected, setSelected] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  });

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(selected)));
    } catch (e) {
      console.error('Failed to persist triage state', e);
    }
  }, [selected]);

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [showOnly, setShowOnly] = useState<'all' | 'selected' | 'unselected'>('all');

  // Pagination — 100 items per page
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [search, category, showOnly]);

  // Unique categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const p of allItems) {
      if (p.category_name) set.add(p.category_name);
      if (p.categories) for (const c of p.categories) set.add(c);
    }
    return ['all', ...Array.from(set).sort()];
  }, [allItems]);

  // Filtered view
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter(p => {
      // Search
      if (q) {
        const name = (p.name || '').toLowerCase();
        const mfr = (p.manufacturer || '').toLowerCase();
        if (!name.includes(q) && !mfr.includes(q)) return false;
      }
      // Category
      if (category !== 'all') {
        const inCatName = p.category_name === category;
        const inCatArr = p.categories?.includes(category);
        if (!inCatName && !inCatArr) return false;
      }
      // Selection filter
      if (showOnly === 'selected' && !selected.has(p.id)) return false;
      if (showOnly === 'unselected' && selected.has(p.id)) return false;
      return true;
    });
  }, [allItems, search, category, showOnly, selected]);

  // Paginated view of filtered items
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [items, safePage]
  );

  // Range selection (shift+click)
  const lastClickedIdxRef = useRef<number | null>(null);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCardClick = useCallback(
    (id: string, idx: number, e: React.MouseEvent) => {
      if (e.shiftKey && lastClickedIdxRef.current !== null) {
        const start = Math.min(lastClickedIdxRef.current, idx);
        const end = Math.max(lastClickedIdxRef.current, idx);
        const rangeIds = pageItems.slice(start, end + 1).map(p => p.id);
        const lastId = pageItems[lastClickedIdxRef.current]?.id;
        const shouldSelect = lastId ? selected.has(lastId) : true;
        setSelected(prev => {
          const next = new Set(prev);
          for (const rid of rangeIds) {
            if (shouldSelect) next.add(rid);
            else next.delete(rid);
          }
          return next;
        });
      } else {
        toggle(id);
      }
      lastClickedIdxRef.current = idx;
    },
    [pageItems, selected, toggle]
  );

  // "Mark all / Keep all" act on the current page only
  const selectAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      for (const p of pageItems) next.add(p.id);
      return next;
    });
  };

  const deselectAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      for (const p of pageItems) next.delete(p.id);
      return next;
    });
  };

  const resetAll = () => {
    // eslint-disable-next-line no-restricted-globals
    if (window.confirm('Clear all selections. Are you sure?')) {
      setSelected(new Set());
    }
  };

  const exportJson = () => {
    const remasterItems = allItems.filter(p => selected.has(p.id));
    const out = {
      exported_at: new Date().toISOString(),
      total_products: allItems.length,
      remaster_count: remasterItems.length,
      keep_count: allItems.length - remasterItems.length,
      remaster: remasterItems.map(p => ({
        id: p.id,
        name: p.name,
        image: p.image,
        category: p.category_name,
        manufacturer: p.manufacturer,
      })),
    };
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `image-triage-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const stats = useMemo(() => {
    const totalSelected = selected.size;
    const filteredSelected = items.filter(p => selected.has(p.id)).length;
    const pageSelected = pageItems.filter(p => selected.has(p.id)).length;
    return {
      total: allItems.length,
      filtered: items.length,
      page: pageItems.length,
      totalSelected,
      filteredSelected,
      pageSelected,
    };
  }, [allItems.length, items, pageItems, selected]);

  if (isLoading) {
    return <div className="text-center py-16 text-warm-400 text-[12px]">Loading products...</div>;
  }

  if (allItems.length === 0) {
    return (
      <div className="text-center py-16 text-warm-400 text-[12px]">
        No products with images found.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sticky filter bar */}
      <div className="sticky top-0 z-20 -mx-6 px-6 pt-1 pb-3 bg-cream-50/95 backdrop-blur-xl border-b border-warm-200/60 lg:-mx-8 lg:px-8">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-warm-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or manufacturer"
              className="w-full pl-9 pr-3 py-1.5 text-[12.5px] text-forest-900 placeholder-warm-400 bg-cream-50 border border-warm-200 rounded-lg focus:outline-none focus:border-forest-400 focus:ring-2 focus:ring-forest-100 transition-all"
            />
          </div>

          {/* Category dropdown */}
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-1.5 text-[12px] text-forest-900 bg-cream-50 border border-warm-200 rounded-lg focus:outline-none focus:border-forest-400 focus:ring-2 focus:ring-forest-100 transition-all max-w-[200px]"
          >
            {categories.map(c => (
              <option key={c} value={c}>
                {c === 'all' ? 'All categories' : formatCategoryName(c)}
              </option>
            ))}
          </select>

          {/* Show only filter — segmented */}
          <div className="inline-flex rounded-lg border border-warm-200 bg-cream-50 p-0.5">
            {(['all', 'unselected', 'selected'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setShowOnly(mode)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  showOnly === mode
                    ? 'bg-forest-700 text-cream-50'
                    : 'text-warm-500 hover:text-forest-800'
                }`}
              >
                {mode === 'all' ? 'All' : mode === 'unselected' ? 'Keep' : 'Marked'}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Stats pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-forest-50 border border-forest-100 rounded-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400" />
            <span className="text-[11px] font-semibold text-forest-900">
              {stats.totalSelected}
            </span>
            <span className="text-[11px] text-forest-600/70">marked</span>
            <span className="text-warm-300">·</span>
            <span className="text-[11px] text-warm-500">
              {stats.total - stats.totalSelected} keep
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={selectAllVisible}
              className="px-2.5 py-1.5 text-[11px] font-medium text-warm-500 hover:text-forest-800 hover:bg-cream-100 rounded-md transition-colors"
              title="Mark all on current page"
            >
              Mark page
            </button>
            <button
              onClick={deselectAllVisible}
              className="px-2.5 py-1.5 text-[11px] font-medium text-warm-500 hover:text-forest-800 hover:bg-cream-100 rounded-md transition-colors"
              title="Keep all on current page"
            >
              Keep page
            </button>
            <button
              onClick={resetAll}
              className="px-2.5 py-1.5 text-[11px] font-medium text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors"
            >
              Reset
            </button>
            <button
              onClick={exportJson}
              disabled={stats.totalSelected === 0}
              className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-semibold text-cream-50 bg-forest-800 hover:bg-forest-900 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export ({stats.totalSelected})
            </button>
          </div>
        </div>

        {/* Subtitle: filter + page context */}
        <div className="mt-2 text-[10.5px] text-warm-400 flex items-center gap-2 flex-wrap">
          <span>
            Page <span className="font-semibold text-forest-700">{safePage}</span>
            <span className="text-warm-300"> / </span>
            {totalPages}
          </span>
          <span className="text-warm-300">·</span>
          <span>
            Showing <span className="font-semibold text-forest-700">{pageItems.length}</span>
            <span className="text-warm-300"> of </span>
            {stats.filtered}
            {stats.filtered !== stats.total && (
              <span className="text-warm-300"> (filtered from {stats.total})</span>
            )}
          </span>
          {stats.pageSelected > 0 && (
            <>
              <span className="text-warm-300">·</span>
              <span className="text-forest-600">
                {stats.pageSelected} marked on this page
              </span>
            </>
          )}
        </div>
      </div>

      {/* Hint */}
      <div className="text-[10.5px] text-warm-400 px-1">
        <span className="inline-flex items-center gap-1">
          <kbd className="px-1 py-0.5 bg-cream-100 border border-warm-300 rounded text-[9px] font-mono text-forest-800">Click</kbd>
          toggle
        </span>
        <span className="mx-2 text-warm-300">·</span>
        <span className="inline-flex items-center gap-1">
          <kbd className="px-1 py-0.5 bg-cream-100 border border-warm-300 rounded text-[9px] font-mono text-forest-800">Shift+Click</kbd>
          range select
        </span>
      </div>

      {/* Grid — matches /shop page card layout exactly */}
      {pageItems.length === 0 ? (
        <div className="text-center py-16 text-warm-400 text-[12px]">
          No products match the current filter.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-7">
          {pageItems.map((p, idx) => {
            const isSelected = selected.has(p.id);
            const imgSrc = resolveImagePath(p.image);
            return (
              <button
                key={p.id}
                onClick={(e) => handleCardClick(p.id, idx, e)}
                className="group relative text-left block"
              >
                {/* Image — identical to ProductCard.tsx shop card */}
                <div
                  className={`relative aspect-[3/4] rounded-xl overflow-hidden mb-4 bg-cream-100 transition-all duration-300 ${
                    isSelected
                      ? 'ring-2 ring-forest-700 ring-offset-2 ring-offset-cream-50'
                      : ''
                  }`}
                >
                  <img
                    src={imgSrc}
                    alt={p.name || 'product'}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-botanical group-hover:scale-[1.04]"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.opacity = '0.25';
                    }}
                  />

                  {/* Dark tint when selected */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-forest-900/30 pointer-events-none transition-opacity duration-300" />
                  )}

                  {/* Quick hover overlay — matches shop */}
                  {!isSelected && (
                    <div className="absolute inset-0 bg-forest-800/0 group-hover:bg-forest-800/[0.03] transition-colors duration-500 pointer-events-none" />
                  )}

                  {/* Checkbox — top right */}
                  <div
                    className={`absolute top-3 right-3 w-[26px] h-[26px] rounded-md flex items-center justify-center transition-all duration-200 ${
                      isSelected
                        ? 'bg-forest-700 border-2 border-forest-700 shadow-[0_2px_10px_rgba(20,43,33,0.4)]'
                        : 'bg-cream-50/85 border-2 border-cream-50 group-hover:border-forest-400 backdrop-blur-sm'
                    }`}
                  >
                    {isSelected && (
                      <svg className="w-4 h-4 text-cream-50" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    )}
                  </div>

                  {/* Marked pill — bottom left */}
                  {isSelected && (
                    <div className="absolute bottom-3 left-3 inline-flex items-center px-2.5 py-1 bg-forest-800 text-cream-50 rounded-md text-nano font-mono font-semibold tracking-wide">
                      REMASTER
                    </div>
                  )}
                </div>

                {/* Info — tight, under card */}
                <div className="space-y-1 px-0.5">
                  <span className="text-nano font-semibold text-forest-600 uppercase tracking-[0.12em]">
                    {formatCategoryName(p.category_name)}
                  </span>
                  <div
                    className={`text-[13px] font-medium leading-snug line-clamp-2 transition-colors ${
                      isSelected ? 'text-forest-900' : 'text-forest-800 group-hover:text-forest-900'
                    }`}
                  >
                    {p.name || '(no name)'}
                  </div>
                  <div className="text-[10.5px] text-warm-400 truncate">
                    {p.manufacturer || '—'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-6 pb-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={safePage === 1}
            className="px-3 py-1.5 text-[12px] font-medium text-warm-500 hover:text-forest-800 hover:bg-cream-100 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => {
            const active = n === safePage;
            return (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`min-w-[32px] h-8 px-2 text-[12px] font-semibold rounded-md transition-all ${
                  active
                    ? 'bg-forest-800 text-cream-50 shadow-[0_2px_8px_-2px_rgba(20,43,33,0.4)]'
                    : 'text-warm-500 hover:text-forest-800 hover:bg-cream-100'
                }`}
              >
                {n}
              </button>
            );
          })}

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            className="px-3 py-1.5 text-[12px] font-medium text-warm-500 hover:text-forest-800 hover:bg-cream-100 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      {/* Bottom spacing */}
      <div className="h-4" />
    </div>
  );
};

export default AdminImageTriage;
